import {
  format,
  isAfter,
  isValid,
  parse,
  startOfDay,
} from "date-fns";

export const DISCONTINUATION_DATE_FORMAT = "MM/dd/yyyy";

export function parseDiscontinuationDateValue(value: string): Date | undefined {
  if (!value?.trim()) return undefined;
  const parsed = parse(value.trim(), DISCONTINUATION_DATE_FORMAT, new Date());
  if (isValid(parsed)) return startOfDay(parsed);
  const fallback = new Date(value);
  return isValid(fallback) ? startOfDay(fallback) : undefined;
}

/** API may send MM/dd/yyyy, ISO string, or epoch seconds/ms. */
export function parseReadyToOrderStatusChangeDate(value: unknown): Date | undefined {
  if (value == null || value === "") return undefined;
  if (value instanceof Date && isValid(value)) return startOfDay(value);
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value > 1e12 ? value : value * 1000;
    const d = new Date(ms);
    return isValid(d) ? startOfDay(d) : undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (/^\d+$/.test(trimmed)) {
      const n = Number(trimmed);
      const ms = n > 1e12 ? n : n * 1000;
      const d = new Date(ms);
      return isValid(d) ? startOfDay(d) : undefined;
    }
    const us = parse(trimmed, DISCONTINUATION_DATE_FORMAT, new Date());
    if (isValid(us)) return startOfDay(us);
    const iso = new Date(trimmed);
    return isValid(iso) ? startOfDay(iso) : undefined;
  }
  return undefined;
}

/** @deprecated No minimum discontinuation date; kept for callers that still import it. */
export function getDiscontinuationMinSelectableDate(
  _readyToOrderStatusChangeDate?: Date
): Date | undefined {
  return undefined;
}

export function getDiscontinuationMaxSelectableDate(): Date {
  return startOfDay(new Date());
}

export function isDiscontinuationDateWithinAllowedRange(date: Date): boolean {
  const day = startOfDay(date);
  const max = getDiscontinuationMaxSelectableDate();
  return !isAfter(day, max);
}

export function clampDiscontinuationDateToAllowedRange(date: Date): Date {
  const day = startOfDay(date);
  const max = getDiscontinuationMaxSelectableDate();
  if (isAfter(day, max)) {
    return max;
  }
  return day;
}

export function formatDiscontinuationDate(date: Date): string {
  return format(startOfDay(date), DISCONTINUATION_DATE_FORMAT);
}

export function getDefaultDiscontinuationDateString(): string {
  return formatDiscontinuationDate(clampDiscontinuationDateToAllowedRange(new Date()));
}

/** Serialized in `reasonForDiscontinuation` when multiple reasons are selected. */
export const DISCONTINUATION_REASON_VALUE_SEPARATOR = "\u001e";

export function parseDiscontinuationReasonSelections(stored: string): string[] {
  const raw = String(stored ?? "").trim();
  if (!raw) return [];
  if (raw.includes(DISCONTINUATION_REASON_VALUE_SEPARATOR)) {
    return raw
      .split(DISCONTINUATION_REASON_VALUE_SEPARATOR)
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [raw];
}

export function serializeDiscontinuationReasonSelections(values: string[]): string {
  return values
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(DISCONTINUATION_REASON_VALUE_SEPARATOR);
}

export function isDiscontinuationReasonOther(reason: string): boolean {
  return parseDiscontinuationReasonSelections(reason).some((part) => {
    const normalized = part.trim().toLowerCase();
    return normalized === "other";
  });
}

const READY_TO_ORDER_STATUS_CHANGE_DATE_KEYS = [
  "readyToOrderStatusChangeDate",
  "readyForOrdersStatusChangeDate",
  "ready_for_orders_status_change_date",
  "vendorAssessmentStatusChangeDate",
] as const;

/** Reads RFO status-change date from API-shaped objects (listing / product info). */
export function resolveReadyToOrderStatusChangeDate(
  source: Record<string, unknown> | null | undefined
): Date | undefined {
  if (!source) return undefined;
  for (const key of READY_TO_ORDER_STATUS_CHANGE_DATE_KEYS) {
    const parsed = parseReadyToOrderStatusChangeDate(source[key]);
    if (parsed) return parsed;
  }
  return undefined;
}
